import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Loader2, CheckCircle2 } from "lucide-react";

import Header from "./Header";
import Footer from "./Footer";
import BackToTop from "./BackToTop";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, formatApiErrorDetail } from "../lib/api";

/**
 * Public forgot-password form. Backend intentionally returns 200 regardless
 * of whether the email exists (to prevent enumeration), so we always render
 * the same "if the email matches an account, we sent a link" success state.
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    if (!email.trim()) {
      setError("Please enter the email on your account.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-cream/40 flex flex-col">
      <Header />
      <main className="flex-1 pt-[88px] sm:pt-[100px] pb-16">
        <div className="max-w-md mx-auto px-5 sm:px-6">
          <Link
            to="/login"
            data-testid="forgot-back-to-login"
            className="inline-flex items-center gap-1.5 text-sm text-brand-charcoal/60 hover:text-brand-teal transition-colors mb-6"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to sign in
          </Link>

          <div className="rounded-3xl bg-white border border-black/5 shadow-sm p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-teal/10 text-brand-teal">
                <Mail className="h-4 w-4" />
              </span>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-brand-teal">
                  Password reset
                </p>
                <h1
                  data-testid="forgot-password-title"
                  className="font-display font-black text-2xl sm:text-3xl text-brand-charcoal leading-tight"
                >
                  Forgot your password?
                </h1>
              </div>
            </div>

            {sent ? (
              <div
                data-testid="forgot-password-sent"
                className="rounded-2xl bg-brand-green/10 border border-brand-green/30 p-5"
              >
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-brand-charcoal text-[15px]">
                      Check your inbox.
                    </p>
                    <p className="text-sm text-brand-charcoal/70 mt-1.5 leading-relaxed">
                      If an account exists for{" "}
                      <strong className="text-brand-charcoal">{email}</strong>, we've
                      emailed a password reset link. The link expires in 60 minutes
                      and can only be used once. Not seeing it? Check your spam
                      folder, then try again.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Link
                        to="/login"
                        data-testid="forgot-password-done"
                        className="inline-flex items-center gap-1.5 rounded-full bg-brand-charcoal text-white px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors"
                      >
                        Return to sign in
                      </Link>
                      <button
                        type="button"
                        onClick={() => {
                          setSent(false);
                          setEmail("");
                        }}
                        data-testid="forgot-password-resend"
                        className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand-charcoal/15 text-brand-charcoal px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] hover:border-brand-teal hover:text-brand-teal transition-colors"
                      >
                        Try a different email
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-brand-charcoal/70 mb-5 leading-relaxed">
                  Enter the email address on your account and we'll send you a
                  secure link to choose a new password. The link expires in 60
                  minutes.
                </p>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="flex flex-col gap-1.5">
                    <Label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
                      Email *
                    </Label>
                    <Input
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="forgot-password-email"
                      placeholder="you@example.com"
                      className="h-11 rounded-xl bg-brand-cream/40 border-black/10"
                    />
                  </div>
                  {error && (
                    <p
                      data-testid="forgot-password-error"
                      role="alert"
                      className="text-[12.5px] text-red-600"
                    >
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="forgot-password-submit"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-3.5 text-[12px] font-bold uppercase tracking-[0.14em] hover:bg-brand-teal transition-colors disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        <Mail className="h-3.5 w-3.5" />
                        Email me a reset link
                      </>
                    )}
                  </button>
                </form>
                <p className="mt-5 text-center text-[11px] text-brand-charcoal/55">
                  Remember your password?{" "}
                  <Link
                    to="/login"
                    className="font-semibold text-brand-teal hover:text-brand-charcoal transition-colors"
                  >
                    Sign in
                  </Link>
                </p>
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}
